import React, { useEffect, useMemo, useState } from "react";
import api from "../utils/api";
import { useNavigate, useParams } from "react-router-dom";
import Select from "react-select";

function toYMD(val) {
  const s = String(val || "").trim();
  if (!s) return "";

  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;

  const dmy = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(s);
  if (dmy) return `${dmy[3]}-${dmy[2]}-${dmy[1]}`;

  return "";
}

function toDMY(val) {
  const s = String(val || "").trim();
  if (!s) return "";

  const onlyDate = s.slice(0, 10);
  const ymd = /^(\d{4})-(\d{2})-(\d{2})$/.exec(onlyDate);
  if (ymd) return `${ymd[3]}/${ymd[2]}/${ymd[1]}`;

  const dmy = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(s);
  if (dmy) return s;

  return "";
}

function num(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

function cleanStr(v) {
  return String(v ?? "").trim();
}

function toArray(data) {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.data)) return data.data;
  if (Array.isArray(data?.items)) return data.items;
  if (Array.isArray(data?.results)) return data.results;
  return [];
}

function getMachineValue(m) {
  if (!m) return "";
  if (typeof m === "string") return m;

  return (
    m.machine_name ||
    m.machineName ||
    m.machine_no ||
    m.machineNo ||
    m.mc_no ||
    m.mcNo ||
    m.name ||
    m.machine ||
    m.code ||
    m.value ||
    ""
  );
}

function getItemCode(it) {
  if (!it) return "";
  return it.item_code || it.itemCode || it.code || it.value || "";
}

function getItemName(it) {
  if (!it) return "";
  return it.item_name || it.itemName || it.name || it.label || "";
}

function getEmpCode(emp) {
  return emp?.emp_code || emp?.empCode || emp?.code || "";
}

function getEmpName(emp) {
  return emp?.emp_name || emp?.empName || emp?.name || "";
}

export default function Crush1Edit() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [date, setDate] = useState("");
  const [receivedBy, setReceivedBy] = useState("");

  const [machines, setMachines] = useState([]);
  const [items, setItems] = useState([]);
  const [employees, setEmployees] = useState([]);

  const [rows, setRows] = useState([
    {
      machineNo: "",
      operatorName: "",
      timeHour: "",
      itemCode: "",
      itemName: "",
      resultKg: "",
      receiveDate: "",
    },
  ]);

  const [loading, setLoading] = useState(true);

  const itemNameByCode = useMemo(() => {
    const map = new Map();
    items.forEach((it) => {
      map.set(getItemCode(it), getItemName(it) || "");
    });
    return map;
  }, [items]);

  useEffect(() => {
    let alive = true;

    (async () => {
      try {
        setLoading(true);

        const [mRes, itemsRes, empRes, hRes, lRes] = await Promise.all([
          api.get("/api/machines?type=Extruder"),
          api.get("/api/items?type=recycle"),
          api.get("/api/employees"),
          api.get("/api/crush/crush1/headers"),
          api.get(`/api/crush/crush1/headers/${id}/lines`),
        ]);

        if (!alive) return;

        const mArr = toArray(mRes.data);
        const itemsArr = toArray(itemsRes.data);
        const empArr = toArray(empRes.data);
        const headersArr = toArray(hRes.data);
        const linesArr = toArray(lRes.data);

        setMachines(mArr);
        setItems(itemsArr);
        setEmployees(empArr);

        const header = headersArr.find(
          (x) => String(x.headerId) === String(id)
        );

        setDate(toDMY(header?.productionDate || ""));
        setReceivedBy(header?.receivedBy || "");

        const mapped = linesArr.map((l) => {
          const itemCode = l.itemCode ?? l.item_code ?? "";
          const masterName =
            itemsArr.find((x) => getItemCode(x) === itemCode)?.item_name || "";

          return {
            machineNo: l.machineNo ?? l.machine_no ?? "",
            operatorName: l.operatorName ?? l.operator_name ?? "",
            timeHour: l.timeHour ?? l.time_hour ?? "",
            itemCode,
            itemName: l.itemName ?? l.item_name ?? masterName,
            resultKg: String(l.resultKg ?? l.result_kg ?? ""),
            receiveDate: toDMY(l.receiveDate ?? l.receive_date ?? ""),
          };
        });

        setRows(
          mapped.length
            ? mapped
            : [
                {
                  machineNo: "",
                  operatorName: "",
                  timeHour: "",
                  itemCode: "",
                  itemName: "",
                  resultKg: "",
                  receiveDate: "",
                },
              ]
        );
      } catch (e) {
        console.error("LOAD ERROR:", e);
        alert(
          e?.response?.data?.sqlMessage ||
            e?.response?.data?.message ||
            e?.response?.data?.error ||
            e?.message ||
            "Failed to load entry"
        );
      } finally {
        if (alive) setLoading(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, [id]);

  const machineOptions = useMemo(() => {
    return machines
      .map((m) => {
        const value = getMachineValue(m);
        return value ? { value, label: value } : null;
      })
      .filter(Boolean);
  }, [machines]);

  const itemCodeOptions = useMemo(() => {
    return items
      .map((it) => {
        const code = getItemCode(it);
        const name = getItemName(it);

        if (!code) return null;

        return {
          value: code,
          label: code,
          searchText: `${code} ${name}`.toLowerCase(),
        };
      })
      .filter(Boolean);
  }, [items]);

  const employeeOptions = useMemo(() => {
    return employees
      .map((emp) => {
        const empCode = getEmpCode(emp);
        const empName = getEmpName(emp);

        if (!empName) return null;

        return {
          value: empName,
          label: empName,
          searchText: `${empName} ${empCode}`.toLowerCase(),
        };
      })
      .filter(Boolean);
  }, [employees]);

  const grandTotalKg = useMemo(() => {
    return rows.reduce((s, r) => s + num(r.resultKg), 0);
  }, [rows]);

  function updateRow(i, k, v) {
    setRows((prev) => {
      const copy = [...prev];
      const row = { ...copy[i], [k]: v };

      if (k === "itemCode") {
        row.itemName = itemNameByCode.get(v) || "";
      }

      copy[i] = row;
      return copy;
    });
  }

  function addRow() {
    setRows((prev) => [
      ...prev,
      {
        machineNo: "",
        operatorName: "",
        timeHour: "",
        itemCode: "",
        itemName: "",
        resultKg: "",
        receiveDate: "",
      },
    ]);
  }

  function removeRow(i) {
    setRows((prev) => (prev.length === 1 ? prev : prev.filter((_, x) => x !== i)));
  }

  async function saveUpdate() {
    try {
      const productionDate = toYMD(date);

      if (!productionDate) return alert("Valid Date required");
      if (!cleanStr(receivedBy)) return alert("Received By required");

      const lines = rows
        .map((r) => ({
          machineNo: cleanStr(r.machineNo),
          operatorName: cleanStr(r.operatorName),
          timeHour: cleanStr(r.timeHour),
          itemCode: cleanStr(r.itemCode),
          itemName: cleanStr(r.itemName),
          resultKg: num(r.resultKg),
          receiveDate: cleanStr(r.receiveDate) ? toYMD(r.receiveDate) : null,
        }))
        .filter(
          (x) =>
            x.machineNo ||
            x.operatorName ||
            x.timeHour ||
            x.itemCode ||
            x.itemName ||
            x.resultKg > 0 ||
            x.receiveDate
        );

      if (!lines.length) return alert("Enter at least one valid row");

      await api.put(`/api/crush/crush1/headers/${id}`, {
        productionDate,
        receivedBy: cleanStr(receivedBy),
        lines,
      });

      alert("Updated successfully");
      navigate("/crush-1");
    } catch (e) {
      console.error("UPDATE ERROR:", e?.response?.data || e);
      alert(
        e?.response?.data?.sqlMessage ||
          e?.response?.data?.message ||
          e?.response?.data?.error ||
          e?.message ||
          "Update failed"
      );
    }
  }

  const customSelectStyles = {
    control: (provided) => ({
      ...provided,
      minHeight: "31px",
      height: "31px",
      border: "1px solid #ced4da",
      boxShadow: "none",
      borderRadius: "0.25rem",
    }),
    valueContainer: (provided) => ({
      ...provided,
      height: "31px",
      padding: "0 8px",
    }),
    input: (provided) => ({
      ...provided,
      margin: "0px",
      padding: "0px",
    }),
    indicatorsContainer: (provided) => ({
      ...provided,
      height: "31px",
    }),
    dropdownIndicator: (provided) => ({
      ...provided,
      padding: "4px",
    }),
    clearIndicator: (provided) => ({
      ...provided,
      padding: "4px",
    }),
    indicatorSeparator: (provided) => ({
      ...provided,
      marginTop: "6px",
      marginBottom: "6px",
    }),
    menu: (provided) => ({
      ...provided,
      zIndex: 9999,
      fontSize: "14px",
    }),
    menuPortal: (provided) => ({
      ...provided,
      zIndex: 9999,
    }),
  };

  if (loading) return <div className="p-3">Loading...</div>;

  return (
    <div className="container-fluid p-3">
      <div className="d-flex justify-content-between align-items-center mb-2">
        <h2 className="mb-0">Edit Crush 1 Entry (ID: {id})</h2>
        <div className="d-flex gap-2">
          <button className="btn btn-dark" onClick={saveUpdate}>
            Save Changes
          </button>
          <button
            className="btn btn-outline-secondary"
            onClick={() => navigate("/crush-1")}
          >
            Close
          </button>
        </div>
      </div>

      <div className="card mb-3">
        <div className="card-body row g-3">
          <div className="col-md-3">
            <label className="form-label">Date</label>
            <input
              type="date"
              className="form-control"
              value={toYMD(date)}
              onChange={(e) => setDate(toDMY(e.target.value))}
            />
          </div>

          <div className="col-md-5">
            <label className="form-label">Received By</label>
            <Select
              options={employeeOptions}
              value={
                employeeOptions.find((opt) => opt.value === receivedBy) || null
              }
              onChange={(selected) =>
                setReceivedBy(selected ? selected.value : "")
              }
              placeholder="-- Select Employee --"
              isClearable
              isSearchable
              styles={customSelectStyles}
              noOptionsMessage={() => "No employee found"}
              filterOption={(option, inputValue) => {
                const text =
                  `${option.label} ${option.data.searchText || ""}`.toLowerCase();
                return text.includes(String(inputValue).toLowerCase());
              }}
              menuPortalTarget={document.body}
              menuPosition="fixed"
            />
          </div>
        </div>
      </div>

      <div className="card">
        <div className="table-responsive">
          <table className="table table-bordered align-middle mb-0">
            <thead className="table-light">
              <tr>
                <th style={{ width: 60 }}>SN</th>
                <th style={{ width: 200 }}>M/C # No.</th>
                <th style={{ width: 220 }}>Operator Name</th>
                <th style={{ width: 160 }}>Time / Hour</th>
                <th style={{ width: 220 }}>Item Code (Recycle)</th>
                <th>Item Name</th>
                <th style={{ width: 140 }}>Result / KG</th>
                <th style={{ width: 170 }}>Receive Date</th>
                <th style={{ width: 180 }}>Action</th>
              </tr>
            </thead>

            <tbody>
              {rows.map((r, i) => (
                <tr key={i}>
                  <td className="text-center">{i + 1}</td>

                  <td>
                    <Select
                      options={machineOptions}
                      value={
                        machineOptions.find((opt) => opt.value === r.machineNo) ||
                        null
                      }
                      onChange={(selected) =>
                        updateRow(i, "machineNo", selected ? selected.value : "")
                      }
                      placeholder="-- Select --"
                      isClearable
                      isSearchable
                      styles={customSelectStyles}
                      noOptionsMessage={() => "No machine found"}
                      menuPortalTarget={document.body}
                      menuPosition="fixed"
                    />
                  </td>

                  <td>
                    <Select
                      options={employeeOptions}
                      value={
                        employeeOptions.find(
                          (opt) => opt.value === r.operatorName
                        ) || null
                      }
                      onChange={(selected) =>
                        updateRow(
                          i,
                          "operatorName",
                          selected ? selected.value : ""
                        )
                      }
                      placeholder="-- Select Employee --"
                      isClearable
                      isSearchable
                      styles={customSelectStyles}
                      noOptionsMessage={() => "No employee found"}
                      filterOption={(option, inputValue) => {
                        const text =
                          `${option.label} ${option.data.searchText || ""}`.toLowerCase();
                        return text.includes(String(inputValue).toLowerCase());
                      }}
                      menuPortalTarget={document.body}
                      menuPosition="fixed"
                    />
                  </td>

                  <td>
                    <input
                      className="form-control form-control-sm"
                      value={r.timeHour}
                      onChange={(e) => updateRow(i, "timeHour", e.target.value)}
                    />
                  </td>

                  <td>
                    <Select
                      options={itemCodeOptions}
                      value={
                        itemCodeOptions.find((opt) => opt.value === r.itemCode) ||
                        null
                      }
                      onChange={(selected) =>
                        updateRow(i, "itemCode", selected ? selected.value : "")
                      }
                      placeholder="-- Select --"
                      isClearable
                      isSearchable
                      styles={customSelectStyles}
                      noOptionsMessage={() => "No item found"}
                      filterOption={(option, inputValue) => {
                        const text =
                          `${option.label} ${option.data.searchText || ""}`.toLowerCase();
                        return text.includes(String(inputValue).toLowerCase());
                      }}
                      menuPortalTarget={document.body}
                      menuPosition="fixed"
                    />
                  </td>

                  <td>
                    <input
                      className="form-control form-control-sm"
                      value={r.itemName}
                      readOnly
                    />
                  </td>

                  <td>
                    <input
                      className="form-control form-control-sm"
                      value={r.resultKg}
                      onChange={(e) => updateRow(i, "resultKg", e.target.value)}
                    />
                  </td>

                  <td>
                    <input
                      type="date"
                      className="form-control form-control-sm"
                      value={toYMD(r.receiveDate)}
                      onChange={(e) =>
                        updateRow(i, "receiveDate", toDMY(e.target.value))
                      }
                    />
                  </td>

                  <td>
                    <div className="d-flex gap-2">
                      <button
                        className="btn btn-sm btn-outline-danger"
                        onClick={() => removeRow(i)}
                      >
                        Remove
                      </button>
                      <button
                        className="btn btn-sm btn-outline-dark"
                        onClick={addRow}
                      >
                        + Add
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="card-footer d-flex justify-content-between">
          <b>Total Records: {rows.length}</b>
          <b>Grand Total KG: {grandTotalKg.toFixed(2)}</b>
        </div>
      </div>
    </div>
  );
}